import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { addDays, format } from "date-fns";
import {
    Button,
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    SearchInput,
    Select,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import {
    displayNameInRightFormat,
    formatDate,
    formatZonedTimeToUtc,
    getStartAndEndOfWeekInAppFormat,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { IConfirmSchedulePayloadForm } from "../../types/dashboard.types";
import { useUpdateSchedule } from "../../hooks/dashboard.mutations";
import useToast from "../../../../hooks/useToast";
import { useDebounce } from "../../../../hooks/helpers";
import { getRecurrenceTag } from "../../helpers/dashboard.helpers";
import moment from "../../../../utils/moment";
import ListState from "../../../../components/ListState/ListState";
import { RecurrenceRule, ScheduleWeekType } from "../../../../utils/types";
import Loader from "../../../../shared-ui/Loader/Loader";
import PlusIcon from "../../../../icons/Plus";
import TimesIcon from "../../../../icons/Times";
import { useFetchWeeklySchedule } from "../../../schedule/hooks/schedule.queries";
import { IWeeklySchedule } from "../../../schedule/types/schedule.types";
import { useAppSelector } from "../../../../hooks/useRedux";

interface ConfirmScheduleFormProps {
    onFormSubmit(): void;
}

type Options = {
    label: string;
    value: string | undefined;
};

const schema = yup.object({
    sessions: yup.array().of(
        yup.object().shape({
            session_id: yup.string(),
            start_date_time: yup
                .string()
                .required("Session start time is required"),
            end_date_time: yup
                .string()
                .required("Session end time is required")
                .test({
                    name: "is-greater",
                    message:
                        "Session end time should be later than the start time",
                    test: (
                        value: string | undefined,
                        context: {
                            parent: {
                                start_date_time: string;
                            };
                        }
                    ) => {
                        const { start_date_time: sessionStartTime } =
                            context.parent;
                        if (!sessionStartTime || !value) {
                            return true;
                        }
                        const start = new Date(
                            `1970-01-01T${sessionStartTime}:00`
                        );
                        const end = new Date(`1970-01-01T${value}:00`);
                        return end > start;
                    },
                }),
            session_day: yup.string().required("Field is required"),
            is_checked: yup.boolean(),
            is_editable: yup.boolean(),
            appointment_type: yup.string().when(["is_checked", "is_editable"], {
                is: (is_checked: boolean, is_editable: boolean) =>
                    is_checked === true && is_editable === true,
                then: yup.string().required("Field is required"),
                otherwise: yup.string().notRequired(),
            }),
        })
    ),
    extra_sessions: yup
        .array()
        .of(
            yup.object().shape({
                start_date_time: yup.string().required("Field is required"),
                end_date_time: yup
                    .string()
                    .required("Field is required")
                    .test({
                        name: "is-greater",
                        message: "End time should be later than the start time",
                        test: (
                            value: string | undefined,
                            context: {
                                parent: {
                                    start_date_time: string;
                                };
                            }
                        ) => {
                            const { start_date_time: sessionStartTime } =
                                context.parent;
                            if (!sessionStartTime || !value) {
                                return true;
                            }
                            const start = new Date(
                                `1970-01-01T${sessionStartTime}:00`
                            );
                            const end = new Date(`1970-01-01T${value}:00`);
                            return end > start;
                        },
                    }),
                session_day: yup.string().required("Field is required"),
                appointment_type: yup.string().required("Field is required"),
            })
        )
        .optional()
        .default([]),
});

export default function ConfirmScheduleForm({
    onFormSubmit,
}: ConfirmScheduleFormProps) {
    const [searchString, setSearchString] = React.useState<string>("");
    const [recurrenceRule, setRecurrenceRule] = React.useState<string>("");
    const [weekInDisplay, setWeekInDisplay] = React.useState(
        ScheduleWeekType.THIS_WEEK
    );

    const { practice } = useAppSelector((state) => state.userPractice);

    const { startOfWeekDisplay: startOfWeek, endOfWeekDisplay: endOfWeek } =
        getStartAndEndOfWeekInAppFormat(weekInDisplay, {
            start: "yyyy-MM-dd",
            end: "yyyy-MM-dd",
        });

    const { startOfWeekDisplay, endOfWeekDisplay } =
        getStartAndEndOfWeekInAppFormat(weekInDisplay, {
            start: "MMMM dd, yyyy",
            end: "MMMM dd, yyyy",
        });

    const searchFilter = useDebounce(searchString, 500);

    const params = React.useMemo(
        () => ({
            from_date: startOfWeek,
            to_date: endOfWeek,
            search_string: searchFilter,
            recurrence_rule: recurrenceRule,
            provider_ids: [practice?.provider_id || ""],
            schedule_for_form: true,
        }),
        [
            startOfWeek,
            endOfWeek,
            searchFilter,
            recurrenceRule,
            practice?.provider_id,
        ]
    );

    const {
        data: weeklySchedule,
        isLoading,
        isSuccess,
        error,
    } = useFetchWeeklySchedule(params, Boolean(practice?.provider_id), 300000);

    const daysOfWeek = React.useMemo((): Options[] => {
        return Array.from({ length: 7 }, (_, i) => {
            const date = moment().startOf("isoWeek").add(i, "days");
            return {
                label: date.format("dddd"),
                value: date.toISOString(),
            };
        });
    }, []);

    const formatTimeToCorrectFormat = (time: number) => {
        if (time < 10) return `0${time}`;
        return time;
    };

    const {
        control,
        handleSubmit,
        register,
        watch,
        reset,
        formState: { errors },
    } = useForm<IConfirmSchedulePayloadForm>({
        resolver: yupResolver(schema),
        values: {
            sessions: isSuccess
                ? weeklySchedule?.data.map((session) => ({
                      session_id: session.session_id,
                      session_day: daysOfWeek.find((day) =>
                          day.label.startsWith(
                              session.confirmed_start_date_time
                                  ? formatDate(
                                        session.confirmed_start_date_time,
                                        false,
                                        "EEE"
                                    )
                                  : session.session_day
                          )
                      )?.value as string,
                      start_date_time: session.confirmed_start_date_time
                          ? formatDate(
                                session.confirmed_start_date_time,
                                false,
                                "HH:mm"
                            )
                          : `${formatTimeToCorrectFormat(
                                session.session_start_hr
                            )}:${formatTimeToCorrectFormat(
                                session.session_start_min
                            )}`,
                      end_date_time: session.confirmed_end_date_time
                          ? formatDate(
                                session.confirmed_end_date_time,
                                false,
                                "HH:mm"
                            )
                          : `${formatTimeToCorrectFormat(
                                session.session_end_hr
                            )}:${formatTimeToCorrectFormat(
                                session.session_end_min
                            )}`,
                      appointment_type:
                          session?.therapy_location &&
                          ["in_person", "remote"].includes(
                              session.therapy_location
                          )
                              ? session.therapy_location
                              : undefined,
                      is_checked: Boolean(session.confirmed_start_date_time),
                      is_editable: session.is_editable,
                      session_history_id:
                          session.session_history_id ?? undefined,
                  }))
                : [],
            extra_sessions: [],
        },
    });

    const {
        fields: extraSessions,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "extra_sessions",
    });

    const doesClientHaveTwoSessions = (clientId: string) => {
        const extraSessionsForm = watch("extra_sessions") || [];
        const sessionsForm = watch("sessions") || [];

        // Convert Set to Array and combine with extra session IDs
        const extraSessionIds = extraSessionsForm
            .map((session) => session.session_id)
            .filter(Boolean);

        const sessionFormIds = sessionsForm
            .filter((session) => session.is_checked)
            .map((session) => session.session_id);

        const allSessionIds = [...sessionFormIds, ...extraSessionIds];

        // Count sessions for the specific client
        const clientSessionCount = allSessionIds.filter((sessionId) => {
            const sessionFromApi = weeklySchedule?.data?.find(
                (session) => session.session_id === sessionId
            );
            return sessionFromApi?.client?.client_id === clientId;
        }).length;

        return clientSessionCount >= 2;
    };

    const handleAddExtraSession = React.useCallback(
        (sessionId: string, parentSessionIndex: number) => {
            append({
                session_id: sessionId,
                parent_session_index: parentSessionIndex,
                start_date_time: "",
                end_date_time: "",
                session_day: "",
                appointment_type: "",
                is_checked: true,
            });
        },
        [append]
    );

    const handleRemoveExtraSession = React.useCallback(
        (sessionIndex: number) => {
            remove(sessionIndex);
        },
        [remove]
    );

    const getDayOrder = (dayAbbreviation: string) => {
        const dayOrder = {
            Mon: 1,
            Tue: 2,
            Wed: 3,
            Thu: 4,
            Fri: 5,
            Sat: 6,
            Sun: 7,
        };
        return dayOrder[dayAbbreviation as keyof typeof dayOrder];
    };

    const timeToMinutes = (hour: number, minute: number) => {
        return hour * 60 + minute;
    };

    const sortSessions = React.useCallback((sessionList: IWeeklySchedule[]) => {
        return (
            sessionList.sort((a, b) => {
                const dayOrderA = getDayOrder(
                    a.confirmed_start_date_time
                        ? formatDate(a.confirmed_start_date_time, false, "EEE")
                        : a.session_day
                );
                const dayOrderB = getDayOrder(
                    b.confirmed_start_date_time
                        ? formatDate(b.confirmed_start_date_time, false, "EEE")
                        : b.session_day
                );

                if (dayOrderA !== dayOrderB) {
                    return dayOrderA - dayOrderB;
                }

                const timeA = timeToMinutes(
                    a.confirmed_start_date_time
                        ? Number(
                              formatDate(
                                  a.confirmed_start_date_time,
                                  false,
                                  "H"
                              )
                          )
                        : a.session_start_hr,
                    a.confirmed_start_date_time
                        ? Number(
                              formatDate(
                                  a.confirmed_start_date_time,
                                  false,
                                  "m"
                              )
                          )
                        : a.session_start_min
                );
                const timeB = timeToMinutes(
                    b.confirmed_start_date_time
                        ? Number(
                              formatDate(
                                  b.confirmed_start_date_time,
                                  false,
                                  "H"
                              )
                          )
                        : b.session_start_hr,
                    b.confirmed_start_date_time
                        ? Number(
                              formatDate(
                                  b.confirmed_start_date_time,
                                  false,
                                  "m"
                              )
                          )
                        : b.session_start_min
                );

                return timeA - timeB;
            }) || []
        );
    }, []);

    const recurrenceRuleForSelect = Object.values(RecurrenceRule).filter(
        (item) =>
            item !== RecurrenceRule.TWO_MONTHS &&
            item !== RecurrenceRule.THREE_MONTHS
    );

    const appointmentTypeForSelect = [
        {
            label: "In Person",
            value: "in_person",
        },
        {
            label: "Remote",
            value: "remote",
        },
    ];

    const currentWeekRange = `${startOfWeekDisplay} - ${endOfWeekDisplay}`;

    const handleRecurrenceChange = (value: string) => {
        setRecurrenceRule(value);
    };

    const { mutate } = useUpdateSchedule();

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const getExtraSessionsForSession = React.useCallback(
        (sessionIndex: number) => {
            return extraSessions.filter(
                (s) => s.parent_session_index === sessionIndex
            );
        },
        [extraSessions]
    );

    const formSessions = watch("sessions");

    const getCheckedSessionsCount = isLoading
        ? 0
        : (formSessions || []).filter((session) => session.is_checked).length;

    const onSubmit = (data: IConfirmSchedulePayloadForm) => {
        // if (getCheckedSessionsCount === 0) {
        //     toast({
        //         mode: "error",
        //         message: "Please select at least one session",
        //     });
        //     return;
        // }

        const dayOffsets: Record<string, number> = {
            Monday: 0,
            Tuesday: 1,
            Wednesday: 2,
            Thursday: 3,
            Friday: 4,
            Saturday: 5,
            Sunday: 6,
        };

        const selectedWeekStartDate = new Date(startOfWeekDisplay);

        const formattedSessions =
            data.sessions &&
            data.sessions
                .filter(
                    (sessionData) =>
                        sessionData.is_editable &&
                        (sessionData.session_history_id ||
                            sessionData.is_checked)
                )
                .map((sessionData) => {
                    const originalSession = weeklySchedule?.data.find(
                        (s) => s.session_id === sessionData.session_id
                    );
                    const sessionWeekday = sessionData.session_day
                        ? moment(sessionData.session_day).format("dddd")
                        : moment(originalSession?.start_date_time).format(
                              "dddd"
                          );

                    const offset = dayOffsets[sessionWeekday];
                    const sessionDateFromOffSet = format(
                        addDays(selectedWeekStartDate, offset),
                        "yyyy-MM-dd"
                    );

                    const [startHour, startMinute] =
                        sessionData.start_date_time.split(":");
                    const formattedStartTime = formatZonedTimeToUtc(
                        `${sessionDateFromOffSet}T${startHour}:${startMinute}:00.000`
                    );

                    const [endHour, endMinute] =
                        sessionData.end_date_time.split(":");
                    const formattedEndTime = formatZonedTimeToUtc(
                        `${sessionDateFromOffSet}T${endHour}:${endMinute}:00.000`
                    );

                    return {
                        session_id: sessionData.session_id,
                        start_date_time: formattedStartTime,
                        end_date_time: formattedEndTime,
                        appointment_type:
                            sessionData?.appointment_type &&
                            ["in_person", "remote"].includes(
                                sessionData.appointment_type
                            )
                                ? sessionData.appointment_type
                                : undefined,
                        is_checked: sessionData.is_checked,
                        session_history_id:
                            sessionData.session_history_id ?? undefined,
                        session_day: sessionDateFromOffSet,
                    };
                });

        const extraSessionsForm = watch("extra_sessions");

        const extraSessionsData =
            extraSessionsForm && extraSessions.length > 0
                ? extraSessionsForm.map((sessionData) => {
                      const originalSession = weeklySchedule?.data.find(
                          (s) => s.session_id === sessionData.session_id
                      );

                      const sessionWeekday = sessionData.session_day
                          ? moment(sessionData.session_day).format("dddd")
                          : moment(originalSession?.start_date_time).format(
                                "dddd"
                            );

                      const offset = dayOffsets[sessionWeekday];
                      const sessionDateFromOffSet = format(
                          addDays(selectedWeekStartDate, offset),
                          "yyyy-MM-dd"
                      );

                      const [startHour, startMinute] =
                          sessionData.start_date_time.split(":");
                      const formattedStartTime = formatZonedTimeToUtc(
                          `${sessionDateFromOffSet}T${startHour}:${startMinute}:00.000`
                      );
                      const [endHour, endMinute] =
                          sessionData.end_date_time.split(":");
                      const formattedEndTime = formatZonedTimeToUtc(
                          `${sessionDateFromOffSet}T${endHour}:${endMinute}:00.000`
                      );
                      return {
                          session_id:
                              sessionData.session_id ??
                              originalSession?.session_id,
                          start_date_time: formattedStartTime,
                          end_date_time: formattedEndTime,
                          appointment_type:
                              sessionData?.appointment_type ?? undefined,
                          is_checked: sessionData.is_checked,
                          session_day: sessionDateFromOffSet,
                      };
                  })
                : [];

        const dataToSend = {
            sessions: [...formattedSessions, ...extraSessionsData],
        };

        const checkedDuplicateItemsList = dataToSend.sessions.filter(
            (item) => item.is_checked
        );

        const hasDuplicates = () => {
            const seen = new Map();

            for (let i = 0; i < checkedDuplicateItemsList.length; i += 1) {
                const item = checkedDuplicateItemsList[i];
                const key = `${item.session_day}-${item.start_date_time}-${item.is_checked}`;

                if (seen.has(key)) {
                    return {
                        hasDuplicate: true,
                        originalIndex: seen.get(key),
                        duplicateIndex: i,
                        sessionDay: item.session_day,
                        client:
                            weeklySchedule?.data?.find(
                                (sc) => sc.session_id === item.session_id
                            )?.client ?? null,
                        startTime: item.start_date_time,
                    };
                }
                seen.set(key, i);
            }

            return { hasDuplicate: false };
        };

        const duplicateCheck = hasDuplicates();

        if (duplicateCheck?.hasDuplicate) {
            toast({
                mode: "error",
                message: `Duplicate session found on ${formatDate(
                    duplicateCheck.startTime as string,
                    false,
                    "EEE do MMM, hh:mmaa"
                )}`,
            });
            return;
        }

        mutate(dataToSend, {
            onSuccess: async (res) => {
                const hasUnupdatedSessions =
                    res.data.unupdated_sessions.length > 0;

                if (hasUnupdatedSessions) {
                    toast({
                        mode: "warning",
                        duration: 12000,
                        className: "w-1/2",
                        message: (
                            <div>
                                {res.message || "Schedule(s) confirmed"}
                                {res.data.unupdated_sessions.length &&
                                res.data.unupdated_sessions.length > 0 ? (
                                    <div className="mt-1 flex flex-col gap-y-1">
                                        {res.data.unupdated_sessions.map(
                                            (s) => (
                                                <div key={s.session_id}>
                                                    {s.error}
                                                </div>
                                            )
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        ),
                    });
                } else {
                    toast({
                        mode: "success",
                        message: res.message || "Schedule(s) confirmed",
                    });
                }

                queryClient.invalidateQueries({
                    queryKey: [`weekly-schedule`],
                });

                queryClient.invalidateQueries({
                    queryKey: ["caseload"],
                });

                queryClient.invalidateQueries({
                    queryKey: ["provider-upcoming-sessions"],
                });

                queryClient.invalidateQueries({
                    queryKey: ["calendar"],
                });

                if (!hasUnupdatedSessions) {
                    onFormSubmit();
                }
            },
            onError: (err) => {
                const unupdatedSessions =
                    err?.response?.data?.data?.unupdated_sessions;

                toast({
                    mode: "error",
                    duration: 12000,
                    className: "w-1/2",
                    message: (
                        <div>
                            {unupdatedSessions && unupdatedSessions.length > 0
                                ? unupdatedSessions.map((s) => (
                                      <div key={s.session_id}>{s.error}</div>
                                  ))
                                : "Could not confirm schedule"}
                        </div>
                    ),
                });
            },
        });
    };

    return (
        <div>
            <div className="text-base font-medium mb-[--spacing-items] flex items-center justify-between gap-[--spacing-items]">
                {`${getCheckedSessionsCount} Sessions selected for: ${currentWeekRange}`}
                <div className="shrink-0">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {removeEnumUnderscore(weekInDisplay)}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent align="end" width={150}>
                            {Object.values(ScheduleWeekType).map((type) => (
                                <DropdownItem
                                    key={type}
                                    onClick={() => {
                                        setWeekInDisplay(type);
                                    }}
                                    isSelected={type === weekInDisplay}
                                >
                                    {removeEnumUnderscore(type)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            </div>
            <div className="flex items-center gap-x-2 justify-between gap-y-[--spacing-items] flex-wrap mb-[--spacing-items]">
                <SearchInput
                    placeholder="Search by client name"
                    containerClass="w-[250px]"
                    onChange={(e) => {
                        reset();
                        setSearchString(e.target.value);
                    }}
                />
                <Dropdown>
                    <DropdownTrigger asChild>
                        <FilterButton>
                            {recurrenceRule
                                ? truncateString(
                                      removeEnumUnderscore(recurrenceRule)
                                  )
                                : "All"}
                        </FilterButton>
                    </DropdownTrigger>
                    <DropdownContent
                        align="end"
                        width={200}
                        maxHeight={216}
                        className="overflow-y-auto"
                    >
                        <DropdownItem
                            isSelected={recurrenceRule === ""}
                            onClick={() => handleRecurrenceChange("")}
                        >
                            All
                        </DropdownItem>
                        {recurrenceRuleForSelect.map((rule) => (
                            <DropdownItem
                                key={rule}
                                isSelected={recurrenceRule === rule}
                                onClick={() => handleRecurrenceChange(rule)}
                            >
                                {removeEnumUnderscore(rule)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            </div>
            {isLoading && <Loader />}
            {error ? (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display sessions at this time please try again later`
                    }
                    context="general"
                    cardClass="h-[150px]"
                />
            ) : null}
            {isSuccess && (
                <form onSubmit={handleSubmit(onSubmit)} id="confirm-schedule">
                    {weeklySchedule.data.length === 0 ? (
                        <ListState
                            isError={false}
                            stateHelperText="Sessions will appear here when available"
                            emptyMessage="No sessions yet"
                            context="general"
                            cardClass="h-[150px]"
                        />
                    ) : null}
                    {weeklySchedule.data.length > 0 ? (
                        <div className="flex flex-col gap-y-2 mt-[--spacing-items]">
                            {sortSessions(weeklySchedule?.data)?.map(
                                (session, i) => {
                                    const isSessionEditable =
                                        session.is_editable;

                                    return (
                                        <Card
                                            key={session.session_id}
                                            classNames="bg-[--card-bg-dark] max-[500px]:overflow-x-auto max-[500px]:max-w-full"
                                        >
                                            <div className="flex justify-between flex-col gap-x-2 gap-y-[--spacing-items]">
                                                <div className="flex items-center gap-x-2">
                                                    <div
                                                        className={
                                                            !isSessionEditable
                                                                ? "[&_span]:opacity-50 [&_span]:cursor-not-allowed [&_input]:opacity-50 [&_input]:cursor-not-allowed "
                                                                : undefined
                                                        }
                                                    >
                                                        <Controller
                                                            name={`sessions.${i}.is_checked`}
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <FormCheckbox
                                                                    disabled={
                                                                        !isSessionEditable
                                                                    }
                                                                    type="checkbox"
                                                                    checked={watch(
                                                                        `sessions.${i}.is_checked`
                                                                    )}
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        field.onChange(
                                                                            e
                                                                                .target
                                                                                .checked
                                                                        );
                                                                    }}
                                                                    title={
                                                                        !isSessionEditable
                                                                            ? "Cannot edit this field"
                                                                            : undefined
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>

                                                    <span className="text-base font-medium">
                                                        {displayNameInRightFormat(
                                                            {
                                                                firstName:
                                                                    session
                                                                        .client
                                                                        .first_name,
                                                                lastName:
                                                                    session
                                                                        .client
                                                                        .last_name,
                                                            }
                                                        )}
                                                    </span>
                                                    <span>
                                                        {getRecurrenceTag(
                                                            session.recurrence_rule
                                                        )}
                                                    </span>
                                                </div>
                                                <div
                                                    className={`flex ${
                                                        errors.sessions?.[i]
                                                            ?.end_date_time ||
                                                        errors.sessions?.[i]
                                                            ?.end_date_time
                                                            ? "items-start"
                                                            : "items-center"
                                                    }  gap-x-2 gap-y-[--spacing-items]`}
                                                >
                                                    <input
                                                        type="hidden"
                                                        {...register(
                                                            `sessions.${i}.session_id`
                                                        )}
                                                        value={
                                                            session.session_id
                                                        }
                                                    />
                                                    <div className="w-auto">
                                                        <Controller
                                                            name={`sessions.${i}.appointment_type`}
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <Select
                                                                    options={
                                                                        appointmentTypeForSelect
                                                                    }
                                                                    placeholder="Location"
                                                                    onChange={(
                                                                        val
                                                                    ) =>
                                                                        field.onChange(
                                                                            (
                                                                                val as Options
                                                                            )
                                                                                .value
                                                                        )
                                                                    }
                                                                    isDisabled={
                                                                        !isSessionEditable
                                                                    }
                                                                    defaultValue={
                                                                        appointmentTypeForSelect.find(
                                                                            (
                                                                                type
                                                                            ) =>
                                                                                type.value ===
                                                                                session.therapy_location
                                                                        ) ??
                                                                        null
                                                                    }
                                                                    isLongListInDialog
                                                                    classNames={{
                                                                        container:
                                                                            () =>
                                                                                "!w-[115px]",
                                                                        menu: () =>
                                                                            "!w-[115px]",
                                                                        menuPortal:
                                                                            () =>
                                                                                "!z-[60]",
                                                                    }}
                                                                    hasError={
                                                                        !!errors
                                                                            .sessions?.[
                                                                            i
                                                                        ]
                                                                            ?.appointment_type
                                                                    }
                                                                    errorText={
                                                                        errors
                                                                            .sessions?.[
                                                                            i
                                                                        ]
                                                                            ?.appointment_type
                                                                            ?.message
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="w-auto">
                                                        <Controller
                                                            name={`sessions.${i}.session_day`}
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <Select
                                                                    options={
                                                                        daysOfWeek
                                                                    }
                                                                    placeholder=""
                                                                    onChange={(
                                                                        val
                                                                    ) =>
                                                                        field.onChange(
                                                                            (
                                                                                val as Options
                                                                            )
                                                                                .value
                                                                        )
                                                                    }
                                                                    isDisabled={
                                                                        !isSessionEditable
                                                                    }
                                                                    defaultValue={daysOfWeek.find(
                                                                        (day) =>
                                                                            day.label.startsWith(
                                                                                session.confirmed_start_date_time
                                                                                    ? formatDate(
                                                                                          session.confirmed_start_date_time,
                                                                                          false,
                                                                                          "EEE"
                                                                                      )
                                                                                    : session.session_day
                                                                            )
                                                                    )}
                                                                    isLongListInDialog
                                                                    classNames={{
                                                                        container:
                                                                            () =>
                                                                                "!w-[115px]",
                                                                        menu: ({
                                                                            placement,
                                                                        }) =>
                                                                            placement ===
                                                                            "top"
                                                                                ? "!w-[115px] !top-auto !bottom-full"
                                                                                : "!w-[115px]",
                                                                        menuPortal:
                                                                            () =>
                                                                                "!z-[60]",
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="w-[115px]">
                                                        <Input
                                                            {...register(
                                                                `sessions.${i}.start_date_time`
                                                            )}
                                                            type="time"
                                                            hasError={
                                                                !!errors
                                                                    .sessions?.[
                                                                    i
                                                                ]
                                                                    ?.start_date_time
                                                            }
                                                            disabled={
                                                                !isSessionEditable
                                                            }
                                                            errorText={
                                                                errors
                                                                    .sessions?.[
                                                                    i
                                                                ]
                                                                    ?.start_date_time
                                                                    ?.message
                                                            }
                                                            value={watch(
                                                                `sessions.${i}.start_date_time`
                                                            )}
                                                            className="w-[115px]"
                                                        />
                                                    </div>
                                                    -
                                                    <div className="w-[115px]">
                                                        <Input
                                                            {...register(
                                                                `sessions.${i}.end_date_time`
                                                            )}
                                                            type="time"
                                                            hasError={
                                                                !!errors
                                                                    .sessions?.[
                                                                    i
                                                                ]?.end_date_time
                                                            }
                                                            disabled={
                                                                !isSessionEditable
                                                            }
                                                            errorText={
                                                                errors
                                                                    .sessions?.[
                                                                    i
                                                                ]?.end_date_time
                                                                    ?.message
                                                            }
                                                            value={watch(
                                                                `sessions.${i}.end_date_time`
                                                            )}
                                                            errorTextClassName="text-small"
                                                            className="w-[115px]"
                                                        />
                                                    </div>
                                                    {watch("sessions")?.[i]
                                                        ?.is_checked ? (
                                                        <Button
                                                            variant="normal"
                                                            size="auto"
                                                            className="h-8 px-2 [border:1px_solid_var(--color-text-link)]"
                                                            onClick={() =>
                                                                handleAddExtraSession(
                                                                    session.session_id,
                                                                    i
                                                                )
                                                            }
                                                            disabled={doesClientHaveTwoSessions(
                                                                session.client
                                                                    .client_id
                                                            )}
                                                        >
                                                            <PlusIcon className="stroke-textLink" />
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            </div>
                                            {getExtraSessionsForSession(i)
                                                ?.length > 0 ? (
                                                <div className="mt-2 flex flex-col gap-y-2">
                                                    {getExtraSessionsForSession(
                                                        i
                                                    ).map((extra) => {
                                                        const itemCorrectIndex =
                                                            extraSessions.findIndex(
                                                                (ext) =>
                                                                    ext.id ===
                                                                    extra.id
                                                            );
                                                        return (
                                                            <div
                                                                key={extra.id}
                                                                className="flex items-center gap-x-2 flex-wrap gap-y-[--spacing-items]"
                                                            >
                                                                <div className="w-auto">
                                                                    <Controller
                                                                        name={`extra_sessions.${itemCorrectIndex}.appointment_type`}
                                                                        control={
                                                                            control
                                                                        }
                                                                        render={({
                                                                            field,
                                                                        }) => (
                                                                            <Select
                                                                                options={
                                                                                    appointmentTypeForSelect
                                                                                }
                                                                                placeholder="Location"
                                                                                onChange={(
                                                                                    val
                                                                                ) => {
                                                                                    field.onChange(
                                                                                        (
                                                                                            val as Options
                                                                                        )
                                                                                            .value
                                                                                    );
                                                                                }}
                                                                                isLongListInDialog
                                                                                classNames={{
                                                                                    container:
                                                                                        () =>
                                                                                            "!w-[115px]",
                                                                                    menu: () =>
                                                                                        "!w-[115px]",
                                                                                    menuPortal:
                                                                                        () =>
                                                                                            "!z-[60]",
                                                                                }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                                <div className="w-auto">
                                                                    <Controller
                                                                        name={`extra_sessions.${itemCorrectIndex}.session_day`}
                                                                        control={
                                                                            control
                                                                        }
                                                                        render={({
                                                                            field,
                                                                        }) => (
                                                                            <Select
                                                                                options={
                                                                                    daysOfWeek
                                                                                }
                                                                                placeholder="Select day"
                                                                                onChange={(
                                                                                    val
                                                                                ) => {
                                                                                    field.onChange(
                                                                                        (
                                                                                            val as Options
                                                                                        )
                                                                                            .value
                                                                                    );
                                                                                }}
                                                                                isLongListInDialog
                                                                                classNames={{
                                                                                    container:
                                                                                        () =>
                                                                                            "!w-[115px]",
                                                                                    menu: ({
                                                                                        placement,
                                                                                    }) =>
                                                                                        placement ===
                                                                                        "top"
                                                                                            ? "!w-[115px] !top-auto !bottom-full"
                                                                                            : "!w-[115px]",
                                                                                    menuPortal:
                                                                                        () =>
                                                                                            "!z-[60]",
                                                                                }}
                                                                                hasError={
                                                                                    !!errors
                                                                                        .extra_sessions?.[
                                                                                        itemCorrectIndex
                                                                                    ]
                                                                                        ?.session_day
                                                                                }
                                                                            />
                                                                        )}
                                                                    />
                                                                </div>
                                                                <div className="w-[115px]">
                                                                    <Input
                                                                        {...register(
                                                                            `extra_sessions.${itemCorrectIndex}.start_date_time`
                                                                        )}
                                                                        type="time"
                                                                        hasError={
                                                                            !!errors
                                                                                .extra_sessions?.[
                                                                                itemCorrectIndex
                                                                            ]
                                                                                ?.start_date_time
                                                                        }
                                                                        className="w-[115px]"
                                                                    />
                                                                </div>
                                                                -
                                                                <div className="w-[115px]">
                                                                    <Input
                                                                        {...register(
                                                                            `extra_sessions.${itemCorrectIndex}.end_date_time`
                                                                        )}
                                                                        type="time"
                                                                        hasError={
                                                                            !!errors
                                                                                .extra_sessions?.[
                                                                                itemCorrectIndex
                                                                            ]
                                                                                ?.end_date_time
                                                                        }
                                                                        className="w-[115px]"
                                                                    />
                                                                </div>
                                                                <Button
                                                                    variant="normal"
                                                                    size="auto"
                                                                    className="h-8 px-2 [border:1px_solid_var(--color-danger)]"
                                                                    onClick={() =>
                                                                        handleRemoveExtraSession(
                                                                            itemCorrectIndex
                                                                        )
                                                                    }
                                                                >
                                                                    <TimesIcon className="stroke-danger" />
                                                                </Button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : null}
                                        </Card>
                                    );
                                }
                            )}
                        </div>
                    ) : null}
                </form>
            )}
        </div>
    );
}
